import { API_URL, WS_URL } from "config";

export type BotsManagementWsEvent = {
  event?: string;
  task?: {
    task_id?: number;
    user_id?: number;
    connection_id?: number;
    job_id?: number | null;
    major_job_id?: number | null;
    task_status?: string | null;
    bot_work_status?: string | null;
    // PlatformUser id of the operator whose click ran the task. Informational
    // here: the popup decision stays row-driven, never event-driven.
    started_by?: number | null;
    started_by_username?: string | null;
  };
  job?: {
    job_id?: number;
    user_id?: number | null;
    major_job_id?: number | null;
  };
  /** A PDF split job of a split major job ("split_job.updated"). */
  split_job?: {
    job_id?: string;
    user_id?: number | null;
    major_job_id?: number | null;
    status?: string | null;
    publish_status?: string | null;
    ssn_check_status?: string | null;
    ssn_updater_status?: string | null;
    discarded?: boolean;
  };
};

type EventListener = (event: BotsManagementWsEvent) => void;
type StatusListener = (connected: boolean) => void;
type AuthRejectedListener = () => void;
type TokenProvider = () => string | null;

const WS_CLOSE_UNAUTHORIZED = 4401;
const WS_CLOSE_FORBIDDEN = 4403;
const HEARTBEAT_INTERVAL_MS = 15000;
// An open socket is not the same as a working one: a stale server (or a channel
// layer that cannot reach this process) leaves it open and silent. The pong the
// server sends back to each ping is the only proof the round trip still works,
// so consumers are told "disconnected" when it stops arriving - that is what
// their polling fallback is for. Silence is measured in unanswered pings, not
// in wall-clock time: a hidden tab has its timers throttled to a tick a minute,
// and "no pong for 45 s" then condemned healthy sockets every few minutes (each
// reconnect costing a full list refetch). Three pings in a row without an
// answer is still a dead layer within ~45 s in a visible tab.
const HEARTBEAT_MAX_UNANSWERED_PINGS = 3;

class BotsManagementWsManager {
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectDelayMs = 2000;
  private disposed = false;
  private authRejected = false;
  private connected = false;
  private unansweredPings = 0;
  private readonly onVisibilityChange = () => {
    // Back in the foreground: ask for proof of life right away instead of
    // waiting for the next (possibly still throttled) heartbeat tick.
    if (document.visibilityState === "visible") this.sendPing();
  };
  private token: string | null = null;
  private getToken: TokenProvider | null = null;
  private eventListeners = new Set<EventListener>();
  private statusListeners = new Set<StatusListener>();
  private authRejectedListeners = new Set<AuthRejectedListener>();

  subscribe(listener: EventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  subscribeStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  subscribeAuthRejected(listener: AuthRejectedListener): () => void {
    this.authRejectedListeners.add(listener);
    return () => this.authRejectedListeners.delete(listener);
  }

  setTokenProvider(provider: TokenProvider | null) {
    this.getToken = provider;
  }

  isConnected(): boolean {
    return this.connected;
  }

  connect(token: string) {
    if (!token) return;
    // A CONNECTING socket counts too: several consumers call connect() on
    // mount (the app connector, the panel, the task page), and tearing down a
    // handshake in flight to start an identical one produced a disconnect/
    // reconnect pair on every navigation.
    if (
      this.token === token &&
      (this.socket?.readyState === WebSocket.OPEN ||
        this.socket?.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    this.token = token;
    this.disposed = false;
    this.authRejected = false;
    this.teardownSocket();
    this.openSocket();
  }

  disconnect() {
    this.disposed = true;
    this.authRejected = false;
    this.token = null;
    this.teardownSocket();
    this.setConnected(false);
  }

  private resolveToken(): string | null {
    const freshToken = this.getToken?.() ?? null;
    return freshToken || this.token;
  }

  private notifyEvent(event: BotsManagementWsEvent) {
    this.eventListeners.forEach((listener) => listener(event));
  }

  private notifyAuthRejected() {
    this.authRejectedListeners.forEach((listener) => listener());
  }

  private setConnected(next: boolean) {
    if (this.connected === next) return;
    this.connected = next;
    this.statusListeners.forEach((listener) => listener(next));
  }

  private sendPing() {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    if (this.unansweredPings >= HEARTBEAT_MAX_UNANSWERED_PINGS) {
      // Silent socket: report it as down so the UI falls back to polling,
      // and start over rather than sitting on a connection that is mute.
      this.setConnected(false);
      this.socket.close();
      return;
    }
    this.unansweredPings += 1;
    this.socket.send(JSON.stringify({ type: "ping" }));
  }

  private teardownSocket() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.close();
      this.socket = null;
    }
  }

  private scheduleReconnect() {
    if (this.disposed || this.authRejected) return;
    this.reconnectTimer = setTimeout(() => this.openSocket(), this.reconnectDelayMs);
    this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, 30000);
  }

  private openSocket() {
    if (this.disposed || this.authRejected) return;

    const token = this.resolveToken();
    if (!token) return;

    this.token = token;
    const wsBaseRaw = WS_URL || API_URL.replace(/^http:\/\//, "ws://").replace(/^https:\/\//, "wss://");
    const wsBase = wsBaseRaw.replace(/\/+$/, "");
    const wsUrl = `${wsBase}/ws/bots-management/?token=${encodeURIComponent(token)}`;
    this.socket = new WebSocket(wsUrl);
    this.socket.onopen = () => {
      this.unansweredPings = 0;
      this.setConnected(true);
      this.reconnectDelayMs = 2000;
      this.heartbeatTimer = setInterval(() => this.sendPing(), HEARTBEAT_INTERVAL_MS);
      document.addEventListener("visibilitychange", this.onVisibilityChange);
    };
    this.socket.onmessage = (message) => {
      try {
        const payload = JSON.parse(message.data) as
          | BotsManagementWsEvent
          | { type?: string };
        if ((payload as { type?: string })?.type === "pong") {
          this.unansweredPings = 0;
          this.setConnected(true);
          return;
        }
        const event = payload as BotsManagementWsEvent;
        if (event?.event || event?.task || event?.job) {
          // Traffic is proof of life too, not just the heartbeat.
          this.unansweredPings = 0;
          this.notifyEvent(event);
        }
      } catch {
        return;
      }
    };
    this.socket.onerror = () => {
      this.socket?.close();
    };
    this.socket.onclose = (event) => {
      this.setConnected(false);
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
      if (this.disposed) return;

      if (event.code === WS_CLOSE_UNAUTHORIZED || event.code === WS_CLOSE_FORBIDDEN) {
        this.authRejected = true;
        if (event.code === WS_CLOSE_UNAUTHORIZED) {
          this.notifyAuthRejected();
        }
        return;
      }

      this.scheduleReconnect();
    };
  }
}

export const botsManagementWs = new BotsManagementWsManager();
